import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { configureStatusBar } from '../services/native/statusBarService.js';

const STORAGE_KEY = 'step-theme';
const THEME_CHOICES = ['light', 'system', 'dark'];
const THEME_SWITCHING_CLASS = 'theme-switching';

export const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dark' },
];

const ThemeContext = createContext(null);

function normalizeTheme(value) {
  return THEME_CHOICES.includes(value) ? value : 'system';
}

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme() {
  if (typeof window === 'undefined') return 'system';
  return normalizeTheme(window.localStorage.getItem(STORAGE_KEY));
}

function resolveTheme(theme) {
  const choice = normalizeTheme(theme);
  return choice === 'system' ? getSystemTheme() : choice;
}

function releaseThemeSwitchingClass(root) {
  const release = () => root.classList.remove(THEME_SWITCHING_CLASS);

  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(release);
    });
    return;
  }

  release();
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return 'light';
  const root = document.documentElement;
  const choice = normalizeTheme(theme);
  const resolvedTheme = resolveTheme(choice);
  root.classList.add(THEME_SWITCHING_CLASS);
  root.dataset.theme = resolvedTheme;
  root.dataset.themeChoice = choice;
  root.classList.toggle('dark', resolvedTheme === 'dark');
  releaseThemeSwitchingClass(root);
  return resolvedTheme;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(getStoredTheme()));

  useLayoutEffect(() => {
    setResolvedTheme(applyTheme(theme));
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme]);

  useLayoutEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setResolvedTheme(applyTheme('system'));

    media.addEventListener?.('change', handleChange);
    media.addListener?.(handleChange);

    return () => {
      media.removeEventListener?.('change', handleChange);
      media.removeListener?.(handleChange);
    };
  }, [theme]);

  useLayoutEffect(() => {
    void configureStatusBar(resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo(() => ({
    theme,
    resolvedTheme,
    setTheme: (nextTheme) => setThemeState(normalizeTheme(nextTheme)),
  }), [resolvedTheme, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

export function useOptionalTheme() {
  return useContext(ThemeContext);
}
